import React, { useEffect, useMemo, useState } from "react";
import { ActivityCard, PlayButton, ProgressPill } from "../shared";

/**
 * PronounExplorer — "Meet the Twelve" tappable reference grid.
 *
 * Pronouns grouped by person (speaker / spoken-to / spoken-about); tapping a
 * card flips it open: big Arabic, transliteration, meaning, example, audio.
 * Completes when all twelve cards have been opened.
 */
const NUMBER_LABEL = { sg: "singular", dual: "dual", pl: "plural" };
const GENDER_LABEL = { m: "masculine", f: "feminine", both: "" };

function PronounCard({ pronoun, opened, expanded, dualNote, onOpen, audio }) {
  const tags = [NUMBER_LABEL[pronoun.number], GENDER_LABEL[pronoun.gender]].filter(Boolean);
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-expanded={expanded}
      className="text-left rounded-xl px-4 py-3 transition-all hover:brightness-110 w-full"
      style={{
        background: "var(--ink)",
        border: expanded ? "1px solid var(--tag-violet)" : "1px solid var(--panel-line)",
        boxShadow: expanded ? "0 0 0 3px var(--violet-bg)" : "none",
      }}
    >
      <div className="flex items-center justify-between gap-2">
        <span dir="rtl" lang="ar" className="font-arabic text-3xl" style={{ color: opened ? "var(--tag-violet)" : "var(--parchment)" }}>
          {pronoun.arabic}
        </span>
        <span className="flex items-center gap-2">
          {opened && (
            <svg className="w-4 h-4" viewBox="0 0 20 20" fill="none" aria-hidden="true" style={{ color: "var(--teal)" }}>
              <path d="M5 10.5l3.2 3.2L15 7" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
          {audio.audioAllowed && expanded && (
            <PlayButton text={pronoun.example_ar} playingText={audio.playingText} onPlay={audio.play} />
          )}
        </span>
      </div>

      {expanded ? (
        <div className="mt-2">
          <p className="text-sm m-0" style={{ color: "var(--parchment)" }}>
            <span className="font-semibold">{pronoun.translit}</span> — {pronoun.en}
          </p>
          <p dir="rtl" lang="ar" className="font-arabic text-xl mt-1.5 mb-0 text-right" style={{ color: "var(--parchment)" }}>
            {pronoun.example_ar}
          </p>
          <p className="text-xs mt-0.5 mb-0" style={{ color: "var(--parchment-dim)" }}>{pronoun.example_en}</p>
          <p className="text-[0.7rem] uppercase tracking-wide mt-1.5 mb-0" style={{ color: "var(--parchment-dim)" }}>
            {tags.join(" · ")}
          </p>
          {pronoun.number === "dual" && (
            <p className="text-xs mt-1 mb-0 italic" style={{ color: "var(--tag-violet)" }}>{dualNote}</p>
          )}
        </div>
      ) : (
        <p className="text-xs mt-1 mb-0" style={{ color: "var(--parchment-dim)" }}>
          {opened ? pronoun.en : "Tap to reveal"}
        </p>
      )}
    </button>
  );
}

export default function PronounExplorer({ data, onComplete, audio }) {
  const [openedKeys, setOpenedKeys] = useState(() => new Set());
  const [expandedKey, setExpandedKey] = useState(null);

  const groups = useMemo(
    () =>
      [1, 2, 3].map((person) => ({
        person,
        label: data.person_labels[person] || data.person_labels[String(person)],
        pronouns: data.pronouns.filter((p) => p.person === person),
      })),
    [data]
  );

  const total = data.pronouns.length;
  const allOpened = openedKeys.size >= total;

  useEffect(() => {
    if (allOpened) onComplete();
  }, [allOpened, onComplete]);

  const handleOpen = (pronoun) => {
    setOpenedKeys((prev) => new Set(prev).add(pronoun.arabic));
    setExpandedKey((k) => (k === pronoun.arabic ? null : pronoun.arabic));
    audio.play(pronoun.arabic);
  };

  return (
    <ActivityCard>
      <div className="flex items-center justify-end mb-3">
        <ProgressPill>{openedKeys.size}/{total} explored</ProgressPill>
      </div>

      <div className="flex flex-col gap-5">
        {groups.map((group) => (
          <div key={group.person}>
            <div className="flex items-baseline gap-2.5 mb-2.5">
              <span dir="rtl" lang="ar" className="font-arabic text-xl" style={{ color: "var(--gold-bright)" }}>
                {group.label.arabic}
              </span>
              <span className="text-sm font-medium" style={{ color: "var(--parchment)" }}>{group.label.en}</span>
              <span className="text-xs" style={{ color: "var(--parchment-dim)" }}>({group.label.hint})</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
              {group.pronouns.map((pronoun) => (
                <PronounCard
                  key={pronoun.arabic}
                  pronoun={pronoun}
                  opened={openedKeys.has(pronoun.arabic)}
                  expanded={expandedKey === pronoun.arabic}
                  dualNote={data.dual_note}
                  onOpen={() => handleOpen(pronoun)}
                  audio={audio}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </ActivityCard>
  );
}
